import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Grid,
  Tabs,
  Tab,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  Badge,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  IconButton
} from '@mui/material';
import {
  ExpandMore,
  Warning,
  Error,
  CheckCircle,
  Shield,
  Gavel,
  Work,
  Assessment,
  ReportProblem,
  Security,
  MonetizationOn,
  AccountBalance,
  OpenInNew,
  Description,
  BusinessCenter,
  Money,
  Policy,
  VerifiedUser,
  Block
} from '@mui/icons-material';
import { vendorAPI } from '../../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
};

interface ComplianceRiskPanelProps {
  vendorUei: string;
  vendorName: string;
  legalName?: string;
}

const ComplianceRiskPanel: React.FC<ComplianceRiskPanelProps> = ({
  vendorUei,
  vendorName,
  legalName
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [complianceData, setComplianceData] = useState<any>(null);

  const fetchComplianceData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorAPI.getVendorCompliance(vendorUei, vendorName, legalName);
      setComplianceData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vendorUei && (vendorName || legalName)) {
      fetchComplianceData();
    }
  }, [vendorUei, vendorName, legalName]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'error';
      case 'MEDIUM':
        return 'warning';
      case 'LOW':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Loading compliance and risk data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        {error}
      </Alert>
    );
  }

  if (!complianceData) {
    return null;
  }

  const { ofac_screening, legal_history, labor_violations, summary } = complianceData || {};

  // Calculate total risk indicators
  const totalRiskIndicators =
    (legal_history?.indicators?.length || 0) +
    (labor_violations?.indicators?.length || 0) +
    (ofac_screening?.matches?.length > 0 ? 1 : 0);

  const criticalIssues = [
    ...(legal_history?.indicators?.filter((i: any) => i.severity === 'CRITICAL') || []),
    ...(labor_violations?.indicators?.filter((i: any) => i.severity === 'CRITICAL') || []),
    ...(ofac_screening?.matches?.length > 0 ? [{ type: 'OFAC_MATCH', severity: 'CRITICAL' }] : [])
  ];

  return (
    <Box>
      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment /> Compliance & Risk Overview
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h3" color={totalRiskIndicators === 0 ? 'success.main' : 'warning.main'}>
                  {totalRiskIndicators}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Risk Indicators
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Stack spacing={0.5} alignItems="center">
                  {!ofac_screening ? (
                    <>
                      <CheckCircle color="success" sx={{ fontSize: 40 }} />
                      <Typography variant="h6" color="success.main">
                        CLEAR
                      </Typography>
                    </>
                  ) : ofac_screening.status === 'MATCH' ? (
                    <>
                      <Block color="error" sx={{ fontSize: 40 }} />
                      <Typography variant="h6" color="error">
                        MATCH
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckCircle color="success" sx={{ fontSize: 40 }} />
                      <Typography variant="h6" color="success.main">
                        CLEAR
                      </Typography>
                    </>
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  OFAC Status
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h3" color={legal_history?.total_legal_cases > 0 ? 'error.main' : 'success.main'}>
                  {legal_history?.total_legal_cases || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Legal Cases
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h3" color={labor_violations?.total_violation_cases > 0 ? 'error.main' : 'success.main'}>
                  {labor_violations?.total_violation_cases || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Labor Violations
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Critical Issues Alert */}
          {criticalIssues.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Critical Compliance Issues Found</AlertTitle>
              {criticalIssues.length} critical risk indicator(s) require immediate attention.
            </Alert>
          )}

          {/* Clean Record Alert */}
          {totalRiskIndicators === 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle>Clean Compliance Record</AlertTitle>
              No compliance issues found. This vendor has a clean record across OFAC, legal, and labor databases.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Detailed Tabs */}
      <Card>
        <CardContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab
                label="OFAC Screening"
                icon={<Badge badgeContent={ofac_screening?.matches?.length || 0} color="error">
                  <Shield />
                </Badge>}
                iconPosition="start"
              />
              <Tab
                label="Legal History"
                icon={<Badge badgeContent={legal_history?.indicators?.length || 0} color="warning">
                  <Gavel />
                </Badge>}
                iconPosition="start"
              />
              <Tab
                label="Labor Compliance"
                icon={<Badge badgeContent={labor_violations?.indicators?.length || 0} color="warning">
                  <Work />
                </Badge>}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* OFAC Tab */}
          <TabPanel value={tabValue} index={0}>
            {(
              <Box>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  {!ofac_screening || ofac_screening?.status === 'CLEAR' ? (
                    <Chip icon={<CheckCircle />} label="OFAC Clear" color="success" />
                  ) : ofac_screening?.status === 'MATCH' ? (
                    <Chip icon={<Block />} label="OFAC Match Found" color="error" />
                  ) : (
                    <Chip icon={<CheckCircle />} label="OFAC Clear" color="success" />
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Last checked: {ofac_screening?.checked_at ? new Date(ofac_screening.checked_at).toLocaleString() : new Date().toLocaleDateString()}
                  </Typography>
                </Stack>

                {ofac_screening?.matches && ofac_screening.matches.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>SDN Name</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Program</TableCell>
                          <TableCell>Match Score</TableCell>
                          <TableCell>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ofac_screening.matches.map((match: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell>{match.sdn_name}</TableCell>
                            <TableCell>{match.sdn_type}</TableCell>
                            <TableCell>{match.program}</TableCell>
                            <TableCell>
                              <Chip
                                label={`${match.score}%`}
                                color={match.score >= 90 ? 'error' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Button size="small" startIcon={<Security />}>
                                View Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="success">
                    No matches found in OFAC Specially Designated Nationals (SDN) list.
                  </Alert>
                )}
              </Box>
            )}
          </TabPanel>

          {/* Legal History Tab */}
          <TabPanel value={tabValue} index={1}>
            {legal_history && legal_history.indicators && legal_history.indicators.length > 0 ? (
              <Box>
                {legal_history.indicators.map((indicator: any, index: number) => (
                  <Accordion key={index} defaultExpanded={indicator.severity === 'CRITICAL' || indicator.severity === 'HIGH'}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Gavel color={getSeverityColor(indicator.severity) as any} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1">{indicator.description}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {indicator.details.case_type?.replace(/_/g, ' ').toUpperCase()}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={indicator.severity}
                            color={getSeverityColor(indicator.severity) as any}
                            size="small"
                          />
                          <Chip
                            label={`Impact: ${indicator.score_impact}`}
                            variant="outlined"
                            size="small"
                          />
                        </Stack>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {/* Case Statistics */}
                        {(indicator.details.total_cases !== undefined || indicator.details.active_cases !== undefined) && (
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={3}>
                              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                                <Typography variant="h6">{indicator.details.total_cases || 0}</Typography>
                                <Typography variant="caption">Total Cases</Typography>
                              </Paper>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                                <Typography variant="h6" color={indicator.details.active_cases ? 'error.main' : 'text.primary'}>
                                  {indicator.details.active_cases || 0}
                                </Typography>
                                <Typography variant="caption">Active Cases</Typography>
                              </Paper>
                            </Grid>
                            {indicator.details.defendant_count !== undefined && (
                              <Grid item xs={6} sm={3}>
                                <Paper sx={{ p: 1.5, textAlign: 'center' }}>
                                  <Typography variant="h6" color="warning.main">{indicator.details.defendant_count}</Typography>
                                  <Typography variant="caption">As Defendant</Typography>
                                </Paper>
                              </Grid>
                            )}
                          </Grid>
                        )}

                        {/* Case Details */}
                        {indicator.details.cases && indicator.details.cases.length > 0 && (
                          <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" gutterBottom>
                              Case Details:
                            </Typography>
                            <List dense>
                              {indicator.details.cases.map((case_item: any, caseIndex: number) => (
                            <ListItem key={caseIndex}>
                              <ListItemIcon>
                                <Description fontSize="small" />
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">{case_item.name}</Typography>
                                    {case_item.is_defendant && (
                                      <Chip label="Defendant" size="small" color="warning" />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <>
                                    {case_item.court} • Docket: {case_item.docket}
                                    {case_item.nature && (
                                      <>
                                        <br />
                                        Nature: {case_item.nature}
                                      </>
                                    )}
                                    {case_item.filed && (
                                      <>
                                        <br />
                                        Filed: {new Date(case_item.filed).toLocaleDateString()}
                                        {case_item.terminated ? ` • Terminated: ${new Date(case_item.terminated).toLocaleDateString()}` : ' • Status: Active'}
                                      </>
                                    )}
                                  </>
                                }
                              />
                              {case_item.url && (
                                <Link href={case_item.url} target="_blank" rel="noopener">
                                  <IconButton size="small">
                                    <OpenInNew fontSize="small" />
                                  </IconButton>
                                </Link>
                              )}
                            </ListItem>
                          ))}
                            </List>
                          </>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Alert severity="success" icon={<CheckCircle />}>
                No legal cases, bankruptcies, or legal proceedings found for this vendor.
              </Alert>
            )}
          </TabPanel>

          {/* Labor Violations Tab */}
          <TabPanel value={tabValue} index={2}>
            {labor_violations && labor_violations.indicators && labor_violations.indicators.length > 0 ? (
              <Box>
                {labor_violations.indicators.map((indicator: any, index: number) => (
                  <Accordion key={index} defaultExpanded={indicator.severity === 'CRITICAL' || indicator.severity === 'HIGH'}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Work color={getSeverityColor(indicator.severity) as any} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1">{indicator.description}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {indicator.type.replace(/_/g, ' ')}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Chip
                            label={indicator.severity}
                            color={getSeverityColor(indicator.severity) as any}
                            size="small"
                          />
                          {indicator.details.total_back_wages && (
                            <Chip
                              label={formatCurrency(indicator.details.total_back_wages)}
                              color="warning"
                              size="small"
                              icon={<MonetizationOn />}
                            />
                          )}
                        </Stack>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {indicator.details.total_cases !== undefined && (
                          <Grid item xs={6} sm={3}>
                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                              <Typography variant="h6">{indicator.details.total_cases}</Typography>
                              <Typography variant="caption">Total Cases</Typography>
                            </Paper>
                          </Grid>
                        )}
                        {indicator.details.total_employees_affected !== undefined && (
                          <Grid item xs={6} sm={3}>
                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                              <Typography variant="h6">{indicator.details.total_employees_affected}</Typography>
                              <Typography variant="caption">Employees Affected</Typography>
                            </Paper>
                          </Grid>
                        )}
                      </Grid>

                      {indicator.details.all_violations?.violations && (
                        <TableContainer sx={{ mt: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Case ID</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Back Wages</TableCell>
                                <TableCell align="right">Penalties</TableCell>
                                <TableCell>Date</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {indicator.details.all_violations.violations.slice(0, 5).map((v: any, idx: number) => (
                                <TableRow key={idx}>
                                  <TableCell>{v.case_id}</TableCell>
                                  <TableCell>{v.violation_type}</TableCell>
                                  <TableCell align="right">{formatCurrency(v.back_wages)}</TableCell>
                                  <TableCell align="right">{formatCurrency(v.civil_penalties)}</TableCell>
                                  <TableCell>{v.findings_end ? new Date(v.findings_end).toLocaleDateString() : 'N/A'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ) : (
              <Alert severity="success" icon={<CheckCircle />}>
                No wage and hour violations, OSHA violations, or labor compliance issues found.
              </Alert>
            )}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Refresh Button */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<Shield />}
          onClick={fetchComplianceData}
          disabled={loading}
        >
          Refresh Compliance Data
        </Button>
      </Box>

      {/* Disclaimer */}
      <Alert severity="warning" sx={{ mt: 3 }}>
        <AlertTitle>Important Notice</AlertTitle>
        Compliance data is sourced from multiple government databases including OFAC SDN list,
        CourtListener PACER system, and Department of Labor enforcement records. This information
        is provided for due diligence purposes only. Always conduct thorough verification and
        consult with legal counsel before making procurement decisions.
      </Alert>
    </Box>
  );
};

export default ComplianceRiskPanel;